import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheck, FaCrown, FaArrowLeft, FaFire, FaBolt } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { getSocialProofText, getRemainingSpotsText } from '@/lib/socialProof';

// Precios de Fit Evolution (USD)
const PRICE_MONTHLY = 9.99;
const PRICE_ANNUAL = 69.99;
const ANNUAL_EQUIVALENT_MONTHLY = PRICE_MONTHLY * 12; // 119.88
const SAVINGS_PCT = Math.round(
  ((ANNUAL_EQUIVALENT_MONTHLY - PRICE_ANNUAL) / ANNUAL_EQUIVALENT_MONTHLY) * 100
);

const FEATURES = [
  'Plan de entrenamiento completamente personalizado según tu objetivo, nivel, frecuencia y equipo.',
  'Plan de alimentación personalizado con calorías, macros, preferencias, patologías y alergias.',
  '5 recetas diferentes por cada tiempo de comida — todas compatibles con tus requerimientos.',
  'Biblioteca de recetas con fotos, ingredientes exactos, preparación paso a paso y valor nutricional.',
  'Registro de peso corporal, medidas y fotos para ver tu progreso.',
  'Estadísticas de evolución mediante gráficos.',
  'Biblioteca completa de ejercicios en video.',
  'Actualización mensual de tu plan de alimentación.',
  'Acceso a TODAS las herramientas Fit Evolution durante la vigencia de tu suscripción.'
];

export default function FitEvolutionSubscribe() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('annual'); // por defecto la anual (destacada)

  const handleContinue = () => {
    const period = selected === 'monthly' ? 'monthly' : 'yearly';
    // Reutilizamos el checkout existente pasando el period seleccionado
    navigate(`/checkout/fit-evolution?period=${period}`);
  };

  return (
    <div
      className="min-h-screen py-8 px-4 md:px-6 relative"
      style={{ backgroundColor: '#0a0a0a' }}
      data-testid="fit-evolution-subscribe"
    >
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 15% 85%, rgba(var(--brand-primary-glow), 0.14), transparent 55%), radial-gradient(circle at 85% 15%, rgba(0, 240, 255, 0.10), transparent 45%)'
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-white/70 hover:text-white mb-6 hover:bg-white/10"
          data-testid="fit-evo-back"
        >
          <FaArrowLeft className="mr-2" /> Volver
        </Button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <p
            className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3"
            style={{ color: 'var(--brand-primary)' }}
          >
            Nuevo · Fit Evolution
          </p>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 uppercase tracking-tight"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Elige tu{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              suscripción
            </span>
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-base md:text-lg">
            Tu plan evoluciona contigo. Actualizaciones mensuales, progresión automática y
            todo lo que necesitas para transformar tu cuerpo y tus hábitos.
          </p>
        </motion.div>

        {/* Cards mensual vs anual */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* MENSUAL */}
          <motion.button
            type="button"
            onClick={() => setSelected('monthly')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`relative text-left rounded-3xl p-6 md:p-8 transition-all duration-300 ${
              selected === 'monthly' ? 'scale-[1.01]' : 'opacity-90 hover:opacity-100'
            }`}
            style={{
              background: 'rgba(26, 26, 26, 0.8)',
              backdropFilter: 'blur(10px)',
              border:
                selected === 'monthly'
                  ? '2px solid #00F0FF'
                  : '1px solid rgba(255, 255, 255, 0.12)'
            }}
            data-testid="fit-evo-monthly-card"
          >
            <p className="text-sm font-medium mb-1" style={{ color: '#00F0FF' }}>
              Suscripción flexible
            </p>
            <h3
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Plan Mensual
            </h3>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-5xl font-black text-white">${PRICE_MONTHLY}</span>
              <span className="text-gray-400">USD / mes</span>
            </div>
            <p className="text-gray-500 text-sm mb-6">Renovación cada 30 días</p>
            <ul className="space-y-2.5 mb-6">
              {FEATURES.slice(0, 5).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <FaCheck className="text-[#00F0FF] mt-0.5 flex-shrink-0 text-xs" />
                  <span>{f}</span>
                </li>
              ))}
              <li className="text-xs text-gray-500 pl-5">+ 4 características más</li>
            </ul>
            <div
              className={`w-full text-center py-3 rounded-full font-semibold text-sm ${
                selected === 'monthly'
                  ? 'text-black'
                  : 'text-white border border-white/20'
              }`}
              style={
                selected === 'monthly'
                  ? { background: '#00F0FF' }
                  : { background: 'transparent' }
              }
            >
              {selected === 'monthly' ? '✓ Seleccionado' : 'Elegir Mensual'}
            </div>
          </motion.button>

          {/* ANUAL - destacada */}
          <motion.button
            type="button"
            onClick={() => setSelected('annual')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className={`relative text-left rounded-3xl p-6 md:p-8 transition-all duration-300 ${
              selected === 'annual' ? 'scale-[1.02]' : 'hover:scale-[1.005]'
            }`}
            style={{
              background:
                'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.15), rgba(var(--brand-primary-glow), 0.05))',
              backdropFilter: 'blur(10px)',
              border:
                selected === 'annual'
                  ? '2px solid var(--brand-primary)'
                  : '1.5px solid rgba(var(--brand-primary-glow), 0.6)',
              boxShadow: selected === 'annual' ? '0 0 40px rgba(var(--brand-primary-glow),0.25)' : 'none'
            }}
            data-testid="fit-evo-annual-card"
          >
            {/* Badges */}
            <div className="absolute -top-3 left-6 flex gap-2">
              <span
                className="text-white text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider flex items-center gap-1"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
              >
                <FaFire /> AHORRA {SAVINGS_PCT}%
              </span>
              <span
                className="text-white text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider flex items-center gap-1"
                style={{ background: 'rgba(255, 255, 255, 0.15)' }}
              >
                <FaCrown /> Mejor oferta
              </span>
            </div>

            <p className="text-sm font-medium mb-1 mt-2" style={{ color: 'var(--brand-primary)' }}>
              Compromiso 12 meses
            </p>
            <h3
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Plan Anual
            </h3>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-black text-white">${PRICE_ANNUAL}</span>
              <span className="text-gray-400">USD / año</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-gray-500 line-through text-sm">
                ${ANNUAL_EQUIVALENT_MONTHLY.toFixed(2)} USD
              </span>
              <span className="text-[var(--brand-primary)] text-sm font-semibold">
                Hoy solo ${PRICE_ANNUAL}
              </span>
            </div>
            <p className="text-white/80 text-sm mb-6 mt-2 leading-snug">
              <FaBolt className="inline text-[var(--brand-primary)] mr-1" />
              12 meses de acceso pagando mucho menos que renovando cada mes.
            </p>
            {/* Prueba social dinámica (cambia cada semana) */}
            <div
              className="flex items-center gap-1.5 mb-2 rounded-full px-3 py-1.5 text-xs font-semibold w-fit"
              style={{
                background: 'rgba(var(--brand-primary-glow), 0.15)',
                color: 'var(--brand-primary)',
                border: '1px solid rgba(var(--brand-primary-glow), 0.3)'
              }}
              data-testid="subscribe-social-proof"
            >
              <FaFire className="text-xs" />
              <span>Más vendido · {getSocialProofText()}</span>
            </div>
            {/* Urgencia: cupos restantes */}
            <div
              className="flex items-center gap-1.5 mb-4 rounded-full px-3 py-1.5 text-xs font-semibold w-fit"
              style={{
                background: 'rgba(245, 158, 11, 0.15)',
                color: '#FBBF24',
                border: '1px solid rgba(245, 158, 11, 0.3)'
              }}
              data-testid="subscribe-remaining-spots"
            >
              <FaBolt className="text-xs" />
              <span>{getRemainingSpotsText()}</span>
            </div>
            <ul className="space-y-2.5 mb-6">
              {FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                  <FaCheck className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0 text-xs" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div
              className={`w-full text-center py-3 rounded-full font-bold text-sm text-white`}
              style={{
                background:
                  selected === 'annual'
                    ? 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))'
                    : 'transparent',
                border: selected === 'annual' ? 'none' : '1px solid rgba(var(--brand-primary-glow),0.4)'
              }}
            >
              {selected === 'annual' ? '✓ Seleccionado' : 'Elegir Anual'}
            </div>
          </motion.button>
        </div>

        {/* CTA principal */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="max-w-md mx-auto"
        >
          <Button
            onClick={handleContinue}
            className="w-full rounded-full font-bold text-white text-base py-6 hover:scale-[1.01] transition-all"
            style={{
              background:
                selected === 'annual'
                  ? 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))'
                  : 'linear-gradient(135deg, #0EA5E9, #00F0FF)'
            }}
            data-testid="fit-evo-continue"
          >
            {selected === 'annual'
              ? `Continuar con Plan Anual · $${PRICE_ANNUAL}`
              : `Continuar con Plan Mensual · $${PRICE_MONTHLY}/mes`}
          </Button>
          <p className="text-center text-xs text-gray-500 mt-3">
            Cancela cuando quieras · Sin permanencia · Pago seguro con MercadoPago
          </p>
        </motion.div>
      </div>
    </div>
  );
}
